import { useEffect } from 'react';

import { EmptyState, type EmptyStateProps } from '@storebrand-digital/pretzel';

import { useRunOnce, type DistributiveOmit } from '@bank/utils';

import type { Language } from '../language';
import { EmptyStatePrimaryIllustration } from '@storebrand-digital/pretzel/illustrations';

export type NotFoundLayoutProps = DistributiveOmit<
  EmptyStateProps,
  'heading' | 'description' | 'iconEllipseProps'
> &
  Partial<Pick<EmptyStateProps, 'heading' | 'description'>> & {
    language: Language;
    onNotFound: () => void;
  };

const copy = {
  no: {
    heading: 'Vi finner ikke siden..',
    description:
      'Beklager så mye! Det kan være noe feil med lenken, problemer med nettet eller at siden er slettet',
  },
  en: {
    heading: "We can't find the page..",
    description:
      "We're so sorry! There may be something wrong with the link, problems with the network, or the page may have been deleted",
  },
} satisfies Record<Language, { heading: string; description: string }>;

export const NotFoundLayout = ({
  language,
  onNotFound,
  heading,
  description,
  illustration = EmptyStatePrimaryIllustration,
  headingAriaLevel = 1,
  ...rest
}: NotFoundLayoutProps) => {
  const runOnce = useRunOnce();

  useEffect(() => {
    runOnce(onNotFound);
  }, [onNotFound, runOnce]);

  const fallback = copy[language];

  return (
    <EmptyState
      {...rest}
      headingAriaLevel={headingAriaLevel}
      illustration={illustration}
      heading={heading ?? fallback.heading}
      description={description ?? fallback.description}
    />
  );
};
